export type ShortlistLaunchLink =
	| { mode: "full"; url: string }
	| { mode: "builder"; baseUrl: string; queryParameters: Array<{ name: string; value: string }> }

function httpsUrl(value: unknown): string {
	if (typeof value !== "string" || !value.trim() || value.length > 32768)
		throw new Error("Enter an HTTPS URL (up to 32,768 characters)")
	const text = value.trim()
	let url: URL
	try {
		url = new URL(text)
	} catch {
		throw new Error("Enter a valid HTTPS URL")
	}
	if (url.protocol !== "https:" || url.username || url.password)
		throw new Error("Use an HTTPS URL without embedded credentials")
	return text
}

export function validateLaunchLink(input: unknown): ShortlistLaunchLink {
	if (!input || typeof input !== "object" || Array.isArray(input))
		throw new Error("Choose a full link or base URL with query parameters")
	const value = input as Record<string, unknown>
	if (value.mode === "full" && Object.keys(value).every((key) => ["mode", "url"].includes(key)))
		return { mode: "full", url: httpsUrl(value.url) }
	if (value.mode !== "builder" || Object.keys(value).some((key) => !["mode", "baseUrl", "queryParameters"].includes(key)))
		throw new Error("Choose a full link or base URL with query parameters")
	if (!Array.isArray(value.queryParameters) || value.queryParameters.length > 50)
		throw new Error("Use at most 50 query parameters")
	const result: ShortlistLaunchLink = {
		mode: "builder",
		baseUrl: httpsUrl(value.baseUrl),
		queryParameters: value.queryParameters.map((row) => {
			if (
				!row ||
				typeof row !== "object" ||
				Object.keys(row).some((key) => !["name", "value"].includes(key)) ||
				typeof row.name !== "string" ||
				!row.name.trim() ||
				row.name.length > 200 ||
				typeof row.value !== "string" ||
				row.value.length > 16000
			)
				throw new Error("Each parameter needs a name (up to 200 characters) and a value (up to 16,000 characters)")
			return { name: row.name.trim(), value: row.value }
		}),
	}
	if (resolveLaunchLink(result).length > 32768) throw new Error("The completed URL is too long (maximum 32,768 characters)")
	return result
}

export function resolveLaunchLink(link: ShortlistLaunchLink): string {
	if (link.mode === "full") return link.url
	const url = new URL(link.baseUrl)
	for (const { name, value } of link.queryParameters) url.searchParams.append(name, value)
	return url.toString()
}
